import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  ArrowUpward,
  ArrowDownward,
  FileDownload as ExportIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/calculations';
import { getTransactionAPI } from '../../api';
import { downloadTransactionExcel } from '../../utils/fileDownload';
import { exportToCSV } from '../../utils/csvExport';
import toast from 'react-hot-toast';

const SearchResultsTable = ({ results, onViewTransaction }) => {
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const handleDownload = async (row) => {
    try {
      const transactionAPI = getTransactionAPI(row.type);
      const response = await transactionAPI.downloadPDF(row.id);
      downloadTransactionExcel(response, row.type, row.id);
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'typeName',
        header: 'Type',
        cell: info => info.getValue(),
        size: 120
      },
      {
        accessorKey: 'id',
        header: 'Transaction #',
        cell: info => {
          const row = info.row.original;
          return `${row.typeName} # ${info.getValue()}`;
        },
        size: 150
      },
      {
        accessorKey: 'customer',
        header: 'Customer/Account',
        cell: info => {
          const row = info.row.original;
          // For invoices/credits, show account name; for cash/refunds show
          // customer. Both `account` and `customer` are relation objects,
          // not plain strings - drill into the actual name field.
          return row.account?.name || row.customer?.customer || '-';
        },
        size: 180
      },
      {
        accessorKey: 'order_number',
        header: 'Order No.',
        cell: info => info.getValue() || '-',
        size: 120
      },
      {
        accessorKey: 'timestamp',
        header: 'Date',
        cell: info => {
          try {
            return format(new Date(info.getValue()), 'dd/MM/yyyy HH:mm:ss');
          } catch (e) {
            return info.getValue();
          }
        },
        size: 160
      },
      {
        accessorKey: 'subtotal',
        header: 'Subtotal',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        accessorKey: 'vat',
        header: 'VAT',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: info => formatCurrency(info.getValue()),
        size: 100
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => onViewTransaction(row.original)}
              color="primary"
              title="View"
            >
              <ViewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDownload(row.original)}
              color="primary"
              title="Download"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
        size: 100
      }
    ],
    [onViewTransaction]
  );

  const table = useReactTable({
    data: results,
    columns,
    state: {
      sorting,
      pagination
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  if (results.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No results found. Try adjusting your search filters.
        </Typography>
      </Paper>
    );
  }

  const handleExportCSV = () => {
    exportToCSV(results, `search-results-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    toast.success('CSV exported successfully');
  };

  return (
    <Box>
      {/* Results Count */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {table.getRowModel().rows.length} of {results.length} results
        </Typography>

        <Button
          variant="outlined"
          size="small"
          startIcon={<ExportIcon />}
          onClick={handleExportCSV}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none'
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() && (
                        header.column.getIsSorted() === 'asc'
                          ? <ArrowUpward fontSize="small" />
                          : <ArrowDownward fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                hover
                sx={{ '&:hover': { bgcolor: 'action.hover' } }}
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            First
          </Button>
          <Button
            size="small"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="small"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
          <Button
            size="small"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            Last
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </Typography>

          <FormControl size="small">
            <Select
              value={table.getState().pagination.pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map(size => (
                <MenuItem key={size} value={size}>
                  Show {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default SearchResultsTable;
